import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'

import CountUpWrapper from 'src/components/CountUpWrapper'
import Progress from 'src/widgets/goal/Progress'
import { openPortalModal } from 'src/widgets/embed/openPortalModal'
import { injectLocalPortalModal } from '../injectLocalPortalModal'
import All from './All'

const getTotals = (data, showActiveTotals) => {
  const { hybrid_raised_with_matching, total_donors, active_raised, active_donors } = data
  return {
    raised: showActiveTotals ? active_raised : hybrid_raised_with_matching,
    donors: showActiveTotals ? active_donors : total_donors,
  }
}

const People = ({ count, onOpen }) =>
  <button
    type="button"
    className="btn btn-link p-0 align-baseline"
    // Two open paths share this button. The donate widget passes
    // onOpen (openPortalModal-routed) so the modal lifts to the host
    // page when properly embedded; the search widget passes no onOpen
    // and the legacy BS5 data-bs-target/target=#donorFeedModal trigger
    // wires it to the in-page _donor_feed.html.erb partial.
    {...(onOpen
      ? { onClick: onOpen }
      : { 'data-bs-toggle': 'modal', 'data-bs-target': '#donorFeedModal' }
    )}
  >
    <span className="fw-bold">{count}</span>
    {count == 1 ? ' person' : ' people'}
  </button>

const Goal = ({
  i18n, partner, partner_item, partner_items, goal_partner_item,
  loaded_as_child,
  goal_threshold, showActiveTotals, currency, donated,
  className = 'widget-content bg-white p-3 p-sm-4 text-center',
  // When both are provided (donate widget v6), the donor count opens
  // the feed via openPortalModal — host-page-level dialog when
  // properly embedded, local BS5 modal otherwise. When omitted
  // (search widget, until it gets the same plumbing), the legacy
  // #donorFeedModal partial flow takes over.
  blank_modal_src, widget_instance_token,
}) => {
  const [ progress, setProgress ] = useState(() =>
    getTotals(partner_item, showActiveTotals)
  )
  const [ orders, setOrders ] = useState(null)
  const [ more, setMore ] = useState(false)
  const [ donorFeedPortal, setDonorFeedPortal ] = useState(null)

  // The goal lives on the parent (goal_partner_item) for newer
  // parent/child IH fundraisers. Only sourced from partner_item
  // when the donor explicitly loaded a child URL — the niche the
  // 022fb7c45 commit added support for. The previous gate
  // (partner_items.length) conflated "split fundraiser" with "use
  // parent for goal", which broke single-child fundraisers loaded
  // by the parent's external_id, slug, or token (partner_items
  // collapses to [], partner_item collapses to the child, and the
  // child has no goal of its own).
  const { goal } = loaded_as_child ? partner_item : goal_partner_item
  const url = `/widgets/goal/${partner.key}/${partner_item.external_id}?with_orders=1`

  const fetchProgress = async () => {
    const response = await fetch(url, { headers: { Accept: 'application/json' } })
    if (!response.ok) return

    const { orders, more_orders_available, ...json } = await response.json()
    setOrders(orders)
    setMore(more_orders_available)
    setProgress(getTotals(json, showActiveTotals))
  }

  useEffect(() => { fetchProgress() }, [ donated ])

  useEffect(() => {
    const interval = setInterval(fetchProgress, 30000)
    return () => clearInterval(interval)
  }, [])

  // openPortalModal is the opt-in trigger; only wired when both
  // blank_modal_src + widget_instance_token came in via props.
  const openDonorFeed = (blank_modal_src && widget_instance_token) ? async () => {
    const result = await openPortalModal({
      // widget_instance_token suffix mirrors the picker + gift-aid
      // pattern — two donate widgets on the page each get their own
      // cached modal iframe in mode 2.
      id: `donor-feed:${widget_instance_token}`,
      src: blank_modal_src,
      ariaLabel: 'Donors',
      maxWidth: '30rem',
      // 'host' only — in mode 2 the iframe would grow tall and slip
      // the close button below the fold, so cap it + scroll the list
      // inside. In modes 1/3 the modal renders in the donate widget's
      // own frame where the natural BS5 modal scroll handles a long
      // list, matching the pre-migration _donor_feed.html.erb
      // behaviour (no modal-dialog-scrollable).
      scrollWithin: 'host',
      renderLocal: injectLocalPortalModal,
    })
    setDonorFeedPortal(result)
  } : null

  // Two portal targets, two code paths. The legacy path (no
  // openDonorFeed wired) keeps mounting into the server-rendered
  // #DonorFeedRoot div inside the _donor_feed.html.erb partial. The
  // openPortalModal path (donate widget) mounts into the resolved
  // portal target. Whichever fires depends on which trigger lit up.
  const allElement = orders && (
    <All
      feedUrl={url}
      orders={orders}
      more_orders_available={more}
    />
  )

  return (
    <div className={className}>
      {allElement && !openDonorFeed && createPortal(
        allElement,
        document.getElementById('DonorFeedRoot')
      )}
      {allElement && donorFeedPortal && createPortal(
        // modal-header + modal-body wrap on the openPortalModal path
        // because the portal target is .modal-content directly (the
        // local renderer's empty shell) — modal-dialog-scrollable's
        // CSS only kicks scrolling on a .modal-body child, and the
        // header gives back the title + close button the
        // _donor_feed.html.erb partial used to render. hide-in-blank
        // on the close button: mode 2's .plg-dialog shadow-DOM close
        // covers dismissal there, so we hide the React-rendered one;
        // mode 1/3 keep it visible (the class is undefined outside
        // the blank iframe).
        <>
          <div className="modal-header p-3 border-bottom">
            <h5 className="modal-title">Donors</h5>
            <button
              type="button"
              className="btn-close hide-in-blank"
              aria-label="Close"
              onClick={() => donorFeedPortal.close()}
            />
          </div>
          <div className="modal-body p-0">
            {allElement}
          </div>
        </>,
        donorFeedPortal.target
      )}
      {
      !Number(progress.raised)
      ? <p className="mb-0 goal-raised-none">{i18n.t('goal_raised_none')}</p>
      : !Number(goal)
      ? Number(progress.raised) < Number(goal_threshold)
      ? <p className="mb-0 goal-raised-little">{i18n.t('goal_raised_little')}</p>
      : <p className="mb-0 goal-raised-amount">
          <CountUpWrapper
            className="fw-bold"
            amount={progress.raised}
            prefix={currency.symbol}
          />
          {' '}
          <span className="text-nowrap">
            raised by <People count={progress.donors} onOpen={openDonorFeed} />
          </span>
        </p>
      : <div className="goal-with-progress d-flex flex-column gap-2">
          <p className="mb-0">
            <CountUpWrapper
              className="fw-bold"
              amount={progress.raised}
              prefix={currency.symbol}
            />
            {` of ${i18n.formatCurrency(goal, currency)} goal raised `}
            <span className="text-nowrap">
              by <People count={progress.donors} onOpen={openDonorFeed} />
            </span>
          </p>
          <Progress goal={goal} raised={progress.raised} />
        </div>
      }
    </div>
  )
}

export default Goal
